
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format, getDay, isFriday } from "date-fns";
import { TimeSlot, ServiceZone } from '@/types';
import { Clock } from "lucide-react";

interface TimeSlotSelectorProps {
  selectedDate: Date | undefined;
  selectedTimeSlot: TimeSlot | undefined;
  onSelectTimeSlot: (slot: TimeSlot) => void;
  zone?: ServiceZone;
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  selectedDate,
  selectedTimeSlot,
  onSelectTimeSlot,
  zone = ServiceZone.PalmSprings
}) => {
  if (!selectedDate) {
    return (
      <Card className="border-gray-200 bg-white shadow-sm">
        <CardContent className="p-6">
          <p className="font-cousine text-gray-500 text-center py-8">
            Please select a date first
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Determine available time slots based on location and day
  const getAvailableTimeSlots = () => {
    const timeSlots: { id: TimeSlot; label: string; timeRange: string; available: boolean }[] = [
      { 
        id: "earlyMorning", 
        label: "Early Morning", 
        timeRange: "6:00am - 10:00am", 
        available: true 
      },
      { 
        id: "evening", 
        label: "Evening", 
        timeRange: "6:00pm - 8:00pm", 
        available: true 
      }
    ];
    
    // Remove 2PM slot on biweekly Fridays for Palm Springs
    // (this is a rough approximation, would need real date logic for production)
    if (zone === ServiceZone.PalmSprings && isFriday(selectedDate)) {
      // Check if this is likely a biweekly Friday
      // A more robust solution would track actual biweekly schedule
      const dayOfMonth = selectedDate.getDate();
      if (dayOfMonth % 14 < 7) { // Rough biweekly check
        // 2PM slot is reserved for standing appointment
        timeSlots.push({
          id: "afternoonReserved",
          label: "Afternoon - Reserved",
          timeRange: "2:00pm - 5:00pm",
          available: false
        });
      }
    }
    
    return timeSlots;
  };
  
  const availableTimeSlots = getAvailableTimeSlots();
  
  return (
    <Card className="border-gray-200 bg-white shadow-sm">
      <CardContent className="p-6">
        <div className="space-y-4">
          {availableTimeSlots.map((slot) => (
            <Button
              key={slot.id}
              variant={selectedTimeSlot === slot.id ? "default" : "outline"}
              className={`w-full py-6 justify-start ${
                selectedTimeSlot === slot.id 
                  ? "bg-black text-white hover:bg-gray-800" 
                  : "border-gray-300 text-black hover:bg-gray-50"
              } ${!slot.available ? "opacity-50 cursor-not-allowed" : ""} font-cousine`}
              onClick={() => slot.available && onSelectTimeSlot(slot.id as TimeSlot)}
              disabled={!slot.available}
            >
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">{slot.label}</div>
                  <div className="text-sm opacity-70">{slot.timeRange}</div>
                  {!slot.available && (
                    <div className="text-xs text-red-600 mt-1">Reserved</div>
                  )}
                </div>
              </div>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default TimeSlotSelector;
